import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { MessageDTO, MessageFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, DatePickers, IChipsSettingProps } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId } from '../../../shared/environment';
import moment from 'moment';
import { MessageMaintenance } from './messageMaintenance';

interface IMessage {
    data: any;
}

export const Message: React.FC<IMessage> = ({ data }) => {

    const [messageSettingList, setMessageSettingList] = useState<ValueListDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [messageList, setMessageList] = useState<MessageDTO[]>([]);
    const [messageFilterDTO, setMessageFilterDTO] = useState<MessageFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setMessageFilterDTO({
            ...messageFilterDTO,
            integrationId: data.id ?? '',
            sourceId: '',
            flowId: '',
            statusId: '',
            startDateTime: undefined,
            endDateTime: undefined
        } as MessageFilterDTO);
    }, []);

    useEffect(() => {
        handleMessageGetAllViewAsync();
        handleListGetAll(ListId.linksphere.message.statusId, setStatusList);
    }, [messageFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleMessageGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        const interval = setInterval(() => {
            handleMessageGetAllViewAsync();
        }, 15000);
        return () => clearInterval(interval);
    }, [messageFilterDTO]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleMessageGetAllViewAsync = useCallback(async () => {
        if (messageFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('linksphere.message', messageFilterDTO, setActionExecuted, setLoading, setMessageList, showMessage, true);
    }, [messageFilterDTO]);

    const handleClearFilterClick = () => {
        setMessageFilterDTO({
            ...messageFilterDTO,
            integrationId: data.id ?? '',
            sourceId: '',
            flowId: '',
            statusId: '',
            startDateTime: undefined,
            endDateTime: undefined
        } as MessageFilterDTO);
    };

    function handleSearchClick() {
        handleMessageGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateUsers = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        // { id: 'new', text: 'New Message', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: messageList.length },
        { label: 'Start Execution', value: messageFilterDTO?.startDateTime ? moment(messageFilterDTO.startDateTime).format('DD/MM/YYYY') : '' },
        { label: 'End Execution', value: messageFilterDTO?.endDateTime ? moment(messageFilterDTO.endDateTime).format('DD/MM/YYYY') : '' },
        { label: 'Status', value: statusList.find(x => x.id === messageFilterDTO?.statusId)?.value },

    ];

    const chipStatus : IChipsSettingProps[] = [
        { value: 'Waiting for processing', iconName: 'schedule_outlined', iconColor: 'primary' },
        { value: 'Processing', iconName: 'update_outlined', iconColor: 'info' },
        { value: 'Successfully integrated', iconName: 'check_circle_outlined', iconColor: 'success' },
        { value: 'Integration failed', iconName: 'running_with_errors_outlined', iconColor: 'error' },
        { value: 'Reprocessing Integration', iconName: 'manage_history_outlined', iconColor: 'warning' }
    ];

    const chipFlow : IChipsSettingProps[] = [
        { value: 'Outbound Message', iconName: 'output_outlined', iconColor: 'success' },
        { value: 'Inbound Message', iconName: 'input_outlined', iconColor: 'warning' },
    ];

    const columns: ICardColumns[] = [
        { field: 'correlationId', label: 'Correlation Id', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'status', label: 'Status', size: 2, type: 'text', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'flow', label: 'Flow', size: 2, type: 'text', visible: true, isChip: true, chipSettings: chipFlow, hasFieldLine: false, hasToolTip: true },
        { field: 'creationDateTime', label: 'Creation Date Time', size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false },
        { field: 'deliveryConfirmationDateTime', label: 'Delivery Confirmation', size: 2, type: 'dateTime', visible: true, isChip: false, hasFieldLine: false },
        { field: 'elapseTime', label: 'ElapseTime', size: 1, type: 'text', visible: true, isChip: false, hasFieldLine: false },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='MESSAGE'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='integrationId'
                                label='Integration Id'
                                setValue={setMessageFilterDTO}
                                value={messageFilterDTO?.integrationId}
                                obj={messageFilterDTO}
                                valueList={messageSettingList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <SelectField
                                id='statusId'
                                label='Status'
                                setValue={setMessageFilterDTO}
                                value={messageFilterDTO?.statusId}
                                obj={messageFilterDTO}
                                valueList={statusList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePickers
                                id='startDateTime'
                                label='Start Date Time'
                                value={messageFilterDTO?.startDateTime ?? null}
                                setValue={setMessageFilterDTO}
                                obj={messageFilterDTO}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePickers
                                id='endDateTime'
                                label='End Date Time'
                                value={messageFilterDTO?.endDateTime ?? null}
                                setValue={setMessageFilterDTO}
                                obj={messageFilterDTO}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={messageList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <MessageMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <MessageMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'linksphere.message'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};